/** The product name is a placeholder until Dax decides; this is the one constant to change. */
export const PRODUCT_NAME = 'Studio';
export const PRODUCT_TAGLINE = 'video content engine';
/** Access to a workspace: a new organisation starts a trial this long; the operator sets it active or lapsed after. */
export const STUDIO_TRIAL_DAYS = 14;
/** A team invite link stays valid this long. */
export const INVITE_TTL_DAYS = 14;
/** The longest name a clip or project may carry, wherever one is typed, derived or suggested. */
export const ASSET_NAME_MAX_CHARS = 120;
/** Organisation and brand slugs (the tenant key in bucket prefixes and the active-brand header). */
export const SLUG_MAX_LENGTH = 48;
export const BRAND_NAME_MAX_LENGTH = 80;
/** The brand a request acts on; the api validates it against the caller's memberships. */
export const ACTIVE_BRAND_HEADER = 'x-active-brand';
/** Media constants from the build book. */
export const EMBEDDING_DIMENSIONS = 768;
export const PROXY_HEIGHT = 720;
export const PROXY_KEYFRAME_INTERVAL_S = 0.5;
/** Two sizes count as the same shape when their aspects agree this closely: 1080x1920 and 2160x3840 are one box. */
export const ASPECT_TOLERANCE = 0.01;
/** A format value ("upright 9:16") names a clip's shape when their aspects agree this closely. */
export const FORMAT_RATIO_TOLERANCE = 0.03;
/**
 * An analysis guess at or above this confidence is applied as a tag; below it the value is only offered
 * (Dax, 2026-09-23: high and very high preselect, medium stays open).
 */
export const TAG_PRESELECT_CONFIDENCE = 0.8;
export const POSTER_AT_MS = 1000;
export const STRIP_FRAME_EVERY_S = 1;
export const STRIP_FRAME_EVERY_S_AFTER_90S = 2;
export const STRIP_COLUMNS = 10;
export const PEAKS_PER_SECOND = 100;
/**
 * The rate the waveform is measured at, before each hundredth of a second keeps its loudest sample.
 * Resampling filters out everything above half the rate: at 1000 Hz a bell peaking at full scale
 * measured -25.9 dB and drew a flat line, and at 8000 Hz a bicycle bell (-5.2 dB) still read -24 dB,
 * because its energy sits above 8 kHz (2026-09-23). 48 kHz keeps everything anyone can hear.
 */
export const PEAKS_SAMPLE_RATE = 48000;
export const SCENE_THRESHOLD = 0.3;
/**
 * Extracting a sound effect from a clip. The DETECTOR has no minimum: transients run 1 to 20 ms and a
 * light switch's whole event is 20 to 80 ms, so a floor on the event would discard the sounds most
 * worth catching (Dax, 2026-09-20). The floor is on the extract instead, padded with source audio.
 */
export const SFX_PREROLL_MS = 30;
/** How long the level must sit at the local floor before the event counts as over. */
export const SFX_TAIL_MS = 120;
export const SFX_MIN_MS = 150;
export const SFX_MAX_MS = 4000;
/** Short on purpose: a longer ramp at the front would soften the attack, which is the whole sample. */
export const SFX_FADE_IN_MS = 5;
/** Longer than the front, and longer than the render's crossfade ramp: the cut is into a decaying tail. */
export const SFX_FADE_OUT_MS = 20;
/** A sample's audio: 48 kHz to match the render, 16-bit PCM so no encoder pads silence before the transient. */
export const SFX_SAMPLE_RATE = 48_000;
/** How many bars a library card's waveform draws. Wide enough to read, small enough to sit in a row. */
export const AUDIO_WAVEFORM_POINTS = 160;
/** How many library rows one selection may act on at once; the listening job takes them in runs of 40. */
export const AUDIO_SELECT_MAX = 50;
/** How long after asking for a listen the audio views read again, by when the names have usually landed. */
export const AUDIO_DESCRIBE_SETTLE_MS = 6000;
/**
 * How long a person's edits to a saved sound (its words, its handles) settle before the sound is listened
 * to or cut again: a few drags or a heading then a description become one job.
 */
export const AUDIO_EDIT_SETTLE_S = 5;
/** The longest name a person may give a library sound (a numbered rename adds its suffix after it). */
export const AUDIO_NAME_MAX = 120;
/** The category of people's names, filled from the drop; the AI is never asked to name a face. */
export const NAMED_PEOPLE_FACET = 'creator';
/** A spoken line shorter than this is a word or two, not a voiceover, and is never judged for the library. */
export const VOICE_LINE_MIN_MS = 1500;
/**
 * A voiceover take fits the cut when it runs from this much shorter to this much longer than it: short
 * enough is filled by a breath at either end, long enough is trimmed by dragging (Dax, 2026-09-29).
 */
export const VOICE_FIT_SHORT_MS = 2000;
export const VOICE_FIT_LONG_MS = 3000;
/** Clips a run of the voiceover judge hears before it queues itself for the rest. */
export const VOICE_JUDGE_PER_RUN = 20;
/**
 * The audio proxy: what plays, auditions and renders. The original is kept untouched in the private
 * bucket, and this is the light copy in the public one, the same split video already uses. 128 kbps
 * is plenty under a social clip, and turns an 11.7 MB WAV into about a megabyte (Dax, 2026-09-20).
 */
export const AUDIO_PROXY_BITRATE = '128k';
export const AUDIO_PROXY_SAMPLE_RATE = 44_100;
/**
 * Below this mean level a sample is effectively silent, and normalising it would haul its own noise
 * floor up to the target. Those are transcoded as they are.
 */
export const AUDIO_SILENT_MEAN_DB = -50;
export const VAD_SAMPLE_RATE = 16000;
export const VAD_FRAME_SAMPLES = 512;
export const VAD_START_PROB = 0.5;
export const VAD_END_PROB = 0.35;
export const VAD_MIN_SPEECH_MS = 250;
export const VAD_MERGE_GAP_MS = 150;
export const VAD_SPEECH_PAD_MS = 100;
export const QUIET_RMS_DBFS = -45;
export const QUIET_MIN_MS = 300;
export const WORD_GAP_MIN_MS = 120;
export const CUT_SNAP_WINDOW_MS = 600;
export const SCENE_SNAP_TOLERANCE_MS = 80;
/** How far an edge may travel, either way, to land on the NEAREST scene cut. The AI's timestamps are
 * typically within a few frames of the cut; a one-way pull of 600 ms carried edges onto the wrong cut. */
export const SCENE_SNAP_NEAR_MS = 300;
/** Edges snap onto a soft visual change only this close; beyond it the AI's own timestamp stands. */
export const SOFT_SNAP_NEAR_MS = 160;
/** When a clip's probe reported no frame rate. */
export const FALLBACK_FPS = 30;
/**
 * The frame grid. Every frame of the proxy (identical timestamps to the master) is sampled once, this
 * many pixels square, in one decode pass that also records FFmpeg's luma scene score per frame. Edges
 * are then expressed as floor(frame pts in ms): FFmpeg's trim keeps frames at or after the start and
 * before the end, so that one number is exact as an out point for the frame before a cut and as an in
 * point for the frame after it. The browser is told to seek one millisecond inside an edge.
 */
export const FRAME_SAMPLE_PX = 16;
/** Tolerance when matching a millisecond edge back onto the frame grid; frames are never this close. */
export const FRAME_EPS_MS = 1;
/** A cut on the RGB difference signal (0..1 mean absolute difference between consecutive tiny frames). Catches colour-only changes luma detectors miss. */
export const CUT_CHROMA_THRESHOLD = 0.1;
/** A softer visual change, still worth snapping an edge onto but not a scene boundary of its own. */
export const CUT_SOFT_THRESHOLD = 0.12;
/** A cut must stand this many times above the median change of its neighbourhood: motion is sustained, a cut is a spike. */
export const CUT_CONTRAST_RATIO = 3;
/** Frames either side a cut compares against for the contrast rule. */
export const CUT_CONTEXT_FRAMES = 15;
/** Two cuts closer than this are one cut (the higher spike wins). */
export const CUT_MIN_GAP_FRAMES = 3;
/**
 * The sampling ladder. The AI's first pass over a whole clip is economical, at a rate its length decides
 * (firstPassFps), so every timestamp is up to most of one sampling interval out. Precision is bought only
 * where it is needed: a second, windowed pass around a segment at the source frame rate (Gemini accepts at
 * most 24 fps) when the segment is short, primary, or when the frame grid shows more than one cut inside the
 * first pass's uncertainty. A single cut inside it needs no call at all: the edge snaps to it.
 */
/**
 * The first pass looks at about this many frames whatever the clip's length (Dax, 2026-10-05): a short clip
 * is seen densely, where a change of scene a second long matters, and a long take is not paid for frame by
 * frame, since its scenes last minutes and the frame grid places every cut exactly anyway.
 */
export const FIRST_PASS_TARGET_FRAMES = 90;
/** Twice a second at most: a 20-second clip gets 40 frames. */
export const FIRST_PASS_MAX_FPS = 2;
/** Once every 20 seconds at least: a half-hour take still gets 90. */
export const FIRST_PASS_MIN_FPS = 0.05;
export function firstPassFps(durationMs) {
    const seconds = durationMs / 1000;
    if (!(seconds > 0))
        return FIRST_PASS_MAX_FPS;
    const fps = Math.min(FIRST_PASS_MAX_FPS, Math.max(FIRST_PASS_MIN_FPS, FIRST_PASS_TARGET_FRAMES / seconds));
    return Math.round(fps * 1000) / 1000;
}
export const GEMINI_MAX_FPS = 24;
export const REFINE_MIN_FPS = 4;
export function refineSamplingFps(sourceFps) {
    return Math.min(GEMINI_MAX_FPS, Math.max(REFINE_MIN_FPS, Math.round(sourceFps || REFINE_MIN_FPS)));
}
/**
 * How far a first-pass timestamp may be from the truth, as a share of its sampling interval: 700 ms at one
 * frame a second, as measured when the first pass always ran at one. The cuts inside it are the candidates.
 */
export const FIRST_PASS_UNCERTAINTY_SHARE = 0.7;
export function firstPassUncertaintyMs(fps) {
    return Math.round((FIRST_PASS_UNCERTAINTY_SHARE * 1000) / fps);
}
/** A segment this short is always refined; a primary one up to the longer bound; nothing beyond the long bound. */
export const REFINE_SHORT_MS = 1500;
export const REFINE_PRIMARY_MS = 4000;
export const REFINE_LONG_MS = 8000;
/** Context either side of a segment in its refinement window, and the most one call may cover. */
export const REFINE_PAD_MS = 1000;
export const REFINE_WINDOW_MAX_MS = 8000;
/** A refined edge snaps onto a cut only this close (about three frames); beyond that the frame itself stands. */
export const REFINED_SNAP_MS = 120;
/** Refinement calls per clip, primaries and the shortest first. */
export const REFINE_MAX_CALLS = 8;
/**
 * Refinement calls in flight at once. Each is one short window of the proxy and independent of the
 * others; run one after another they were the longest stretch of a run (2026-09-23: eight calls, one of
 * which hung for undici's full 300 s before failing).
 */
export const REFINE_CONCURRENCY = 3;
/** A refinement call slower than this is abandoned and the first-pass edge kept. */
export const REFINE_CALL_TIMEOUT_MS = 90_000;
/** Highlights are the primary moments; with fewer than this, secondaries top them up, then the rest. */
export const HIGHLIGHT_FALLBACK_MIN = 3;
export const SCENE_COLOUR_COUNT = 3;
/** A visual span shorter than this is a sliver and joins the neighbour it resembles most. */
export const MIN_SCENE_MS = 800;
/** Colour-signature similarity (histogram intersection, 0..1) above which two spans share a visual signature. */
export const SCENE_MERGE_SIMILARITY = 0.6;
/** A continuous take longer than this with speech is offered as passages cut at pauses. */
export const PASSAGE_SPAN_MS = 60_000;
export const PASSAGE_TARGET_MS = 45_000;
export const PASSAGE_MIN_MS = 20_000;
/**
 * Video extras: frame-tight extracts that stand out on their own. A colour signature is one dominant
 * colour holding steady, this saturated, with this share of the picture, this still; a pack shot is the
 * stillest run inside a segment the AI flagged as product or packaging filling the frame. Either can be
 * as short as EXTRA_MIN_FRAMES; at most EXTRAS_MAX per clip.
 */
export const EXTRA_MIN_FRAMES = 5;
export const EXTRAS_MAX = 8;
export const COLOUR_STABLE_MAX_DELTA = 0.035;
export const COLOUR_MIN_SATURATION = 0.28;
export const COLOUR_MIN_SHARE = 0.5;
/** Headings on spoken lines and scenes: a few words. */
export const HEADING_MAX_CHARS = 60;
/** A snippet's description, as the analyser's text box allows it. */
export const DESCRIPTION_MAX_CHARS = 500;
/** How many rows one merge may join, on any Analyser tab. */
export const MERGE_MAX_ROWS = 20;
/** Merged rows' descriptions read in time order, joined by this. */
export const MERGED_DESCRIPTION_JOIN = '; ';
/**
 * A re-analysis rebuilds the scenes, so a switched-off scene's range no longer exists. A rebuilt scene
 * stays off when more than this share of it lies inside scenes a person switched off (Dax, 2026-09-25).
 */
export const SCENE_OFF_CARRY_SHARE = 0.5;
/**
 * An edit to an indexed clip re-embeds its rows, at most once per this many seconds; edits inside the
 * window collapse into one job in the next, so the last word typed is always the one indexed.
 */
export const REEMBED_DEBOUNCE_S = 10;
/**
 * An audio row is busy while it is owed its measuring (waveform and length) or its listen (name and
 * filing), for this long after it arrived. Past it, work that has not finished has failed, and the row
 * stops claiming to be busy rather than spinning forever.
 */
export const AUDIO_WORK_WINDOW_MS = 2 * 60 * 60 * 1000;
/**
 * The key a brand-wide audio sweep (tidy, identify, describe, embed) is queued under. Those queues hold at
 * most one waiting and one running job per key (Dax, 2026-09-25): a dropped folder of fifty files used
 * to queue fifty sweeps of the whole brand, and two running at once listened to the same sounds twice.
 * A request for named rows gets its own key, so it is never folded into the sweep.
 */
export function audioJobKey(brandId, bedIds = null) {
    return bedIds?.length ? `${brandId}:${[...bedIds].sort().join(',')}` : `${brandId}:sweep`;
}
export const MIN_SNIPPET_MS = 700;
export const PHRASE_GAP_MS = 700;
export const PHRASE_GAP_FULL_SCORE_MS = 1500;
export const AUDIO_RAMP_MS = 5;
export const LOUDNESS_TARGET_LUFS = -14;
export const SIGNED_URL_TTL_MS = 15 * 60 * 1000;
export const DEFAULT_COOLDOWN_DAYS = 30;
export const DEFAULT_COOLDOWN_POSTS = 5;
export const COMBINATION_WINDOW_DAYS = 30;
export const UNUSED_RANKING_CAP_DAYS = 90;
export const TYPEAHEAD_DEBOUNCE_MS = 120;
/** Every edit autosaves once typing pauses this long; there are no Save buttons (Dax, 2026-09-15). */
export const AUTOSAVE_DEBOUNCE_MS = 1000;
/**
 * How often a screen re-reads work that is still in flight. One number for every such screen: the
 * Analyser's clip and the Upload table used to disagree (4 s against 15 s), so the same finished
 * analysis appeared on one screen and not the other.
 */
export const WORK_POLL_MS = 4000;
/**
 * Above this many files in one drop the upload stops refreshing the table after every file: a sample
 * pack is hundreds of them, and hundreds of refetches for a row nobody is reading yet is waste.
 */
export const UPLOAD_QUIET_ABOVE = 12;
/** How long the uncropped mockup stays up after an aspect button is clicked (Dax, 2026-09-20). */
export const ASPECT_PEEK_MS = 3000;
export const TYPEAHEAD_LIMIT = 20;
/** How many clips the Recent picker and the Analyser's clip list read. */
export const IMPORTS_LIST_LIMIT = 80;
/** How many clips a page of Tagging's Browse table holds, in the order its sorted column asks for. */
export const TAGGED_PAGE_SIZE = 25;
/** How many assets a page of the Library holds; the pager on its filter row steps through the rest. */
export const LIBRARY_PAGE_SIZE = 50;
/** The audio table's rows per page: compact text rows, so a long pack still reads a page at a time. */
export const AUDIO_TABLE_PAGE_SIZE = 100;
/** A name in the audio table is cut at this many characters, the whole of it on hover (Dax, 2026-10-04). */
export const AUDIO_TABLE_NAME_CHARS = 28;
/** Tags shown in an audio table row before its More (three by three). */
export const AUDIO_TABLE_TAGS_SHOWN = 9;
/** The most tags a sound keeps. */
export const AUDIO_TAGS_MAX = 20;
/** The longest one tag may be. */
export const AUDIO_TAG_MAX_CHARS = 40;
/** How many cards a page of Browse holds, across videos, snippets and audio in that order. */
export const BROWSE_PAGE_SIZE = 40;
/** The Browse tab filters on this many of the brand's categories: the first, in its settings order, that carry tags. */
export const TAGGED_FILTER_FACETS = 3;
export const JWKS_TTL_MS = 60 * 60 * 1000;
/** Below this cosine a semantic-only pick is reported as a close match, not a find. */
export const CLOSE_MATCH_MIN_COSINE = 0.55;
/**
 * How far above the query's own background a clip must stand to count as showing what was asked (Dax,
 * 2026-10-04). Footage of one brand is all about the same things, so every clip sits close to any query
 * on meaning: measured on a dessert library, the median clip scored 0.52 to 0.66 depending on the words,
 * a query for something the library never shows still reached 0.61 at its best (0.09 above its median),
 * and real matches stood 0.13 to 0.20 above. An absolute floor cannot tell these apart; the margin can.
 */
export const RELEVANCE_MARGIN = 0.11;
/** Thumbnails on an empty Analyser or Tagging page. */
export const RECENT_FILES_COUNT = 4;
/**
 * The house organisation and brand, fixed in migration 022 so nothing has to look them up: Studio Library,
 * whose audio every tenant can use and whose sound categories are the whole library's vocabulary.
 */
export const HOUSE_ORG_ID = '00000000-0000-0000-0000-00000000f00d';
export const HOUSE_BRAND_ID = '00000000-0000-0000-0000-00000000beef';
//# sourceMappingURL=constants.js.map